import React, { createContext, useContext, ReactNode } from 'react';

interface Theme {
  palette: {
    primary: { main: string };
    secondary: { main: string };
    background: { default: string; paper: string };
    text: { primary: string };
  };
  spacing: (factor: number) => string;
  breakpoints: {
    down: (key: string) => string;
    up: (key: string) => string;
  };
}

/**
 * Custom Theme Context to bypass Material-UI's theme system
 * This completely avoids emotion's attachTheme function
 */
const BypassThemeContext = createContext<Theme | null>(null);

interface BypassThemeProviderProps {
  theme: Theme;
  children: ReactNode;
}

/**
 * Custom Theme Provider that doesn't use Material-UI's ThemeProvider
 */
export const BypassThemeProvider: React.FC<BypassThemeProviderProps> = ({ theme, children }) => {
  return <BypassThemeContext.Provider value={theme}>{children}</BypassThemeContext.Provider>;
};

/**
 * Custom hook to use the bypass theme
 */
export const useBypassTheme = (): Theme => {
  const theme = useContext(BypassThemeContext);
  if (!theme) {
    console.warn('useBypassTheme must be used within a BypassThemeProvider');
    // Return a minimal fallback theme
    return {
      palette: {
        primary: { main: '#1976d2' },
        secondary: { main: '#dc004e' },
        background: { default: '#fff', paper: '#fff' },
        text: { primary: 'rgba(0, 0, 0, 0.87)' }
      },
      spacing: (factor: number) => `${8 * factor}px`,
      breakpoints: {
        down: (key: string) => `@media (max-width:${key === 'md' ? 899 : 599}px)`,
        up: (key: string) => `@media (min-width:${key === 'md' ? 900 : 600}px)`
      }
    };
  }
  return theme;
};

export default BypassThemeProvider;
